import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";

import { resolveInject } from "app/di";
import { IAppConfigProvider } from "app/services/config/app-config-loader.service";
import { ITerm, TermBuilder } from "app/services/data-model";
import { Breadcrumbs } from "app/ui/sections/breadcrumbs";
import { InformationPane } from "app/ui/sections/information.pane";
import { Hierarchy } from "app/ui/sections/hierarchy";
import { TopMenu } from "app/ui/sections/top-menu";
import { parseUrlQuery } from "app/utils/syntax-helpers";
import { SparqlResultParser } from "../../services/infrastructure/sparql-result-parser";
import { ISparqlExecutor } from "../../services/infrastructure/sparql-executor";
import { Resizable, Enable } from "re-resizable";
import { Term } from "app/services/data-model";
import { HierarchyConfig } from "app/services/config/app-config";
import { NavigateService } from "app/services/navigate.service";

export const PATHNAME = "/view";

/*
 * Page component for browsing a hierarchy and seeing information on the
 * currently selected term (stored as an ITerm, which includes an iri, name,
 * and icon). If solely the iri of the current term is known during loading,
 * the further information will be loaded in the 'loadData' function to fill
 * in the missing information, which is then processed and disseminated to
 * child components that depend on the information.
 */

/*
 * Page component for browsing a hierarchy and seeing information on the
 * currently selected term (stored as an ITerm, which includes an iri, name,
 * and icon).
 */
export const BrowsePage: React.FC = () => {
    const location = useLocation();
    const params = useParams();
    const appConfig = resolveInject(IAppConfigProvider);
    const sparqlExecutor = resolveInject(ISparqlExecutor);

    // State
    const [currentTerm, setCurrentTerm] = React.useState<ITerm | undefined>(undefined);
    const [currentHierarchyPath, setCurrentHierarchyPath] = React.useState<ITerm[] | undefined>(
        undefined
    );
    const [hierarchies] = React.useState<HierarchyConfig[]>(appConfig.config.hierarchies);
    const [activeHierarchy, setActiveHierarchy] = React.useState<HierarchyConfig>(
        appConfig.config.hierarchies[0]
    );
    const [selectedVersion, setSelectedVersion] = React.useState<string>(sparqlExecutor.versionUrl);
    const tempHierarchyPath = React.useRef<ITerm[] | undefined>(undefined);
    const [hierarchyWidth, setHierarchyWidth] = React.useState<number>(400); // Initial width for hierarchy column

    // Get URI from location
    const getUri = (): string | undefined => {
        const queryParams = parseUrlQuery(location.search);
        return queryParams.uri || appConfig.config.startTerm || undefined;
    };

    // Get current term from path
    const getCurrentTermFromPath = (path: ITerm[] | undefined): ITerm | undefined => {
        if (path && path.length > 0) {
            return path[path.length - 1];
        }
        return undefined;
    };

    // Get current term from URI
    const getCurrentTermFromUri = (uri: string | undefined): ITerm | undefined => {
        if (uri && uri.length > 0) {
            return { iri: uri, name: "" };
        }
        return undefined;
    };

    // Load term data
    const loadData = React.useCallback(
        async (term: ITerm | undefined) => {
            if (term && !term.name) {
                try {
                    const sparqlResponse = await sparqlExecutor.execute(
                        appConfig.config.queryTerm,
                        [{ name: "term_iri", type: "uri", value: term.iri }]
                    );
                    const result = SparqlResultParser.parse(sparqlResponse);
                    const updatedTerm =
                        result && result.length > 0
                            ? TermBuilder.parseFromSparqlRecord(result[0])
                            : { iri: term.iri, name: Term.getLocalname(term.iri) };
                    if (updatedTerm && updatedTerm.iri === term.iri) {
                        setCurrentTerm(updatedTerm);
                    }
                } catch (ex) {
                    console.log(ex); // unable to properly load term
                }
            }
        },
        [appConfig.config.queryTerm, location.hash, sparqlExecutor]
    );

    React.useEffect(() => {
        const newUri = getUri();

        if (!newUri) {
            setCurrentTerm(undefined);
            setCurrentHierarchyPath(undefined);
            return;
        }

        if (currentTerm?.iri !== newUri) {
            const pathTerm = getCurrentTermFromPath(tempHierarchyPath.current);
            if (pathTerm?.iri == newUri) {
                // use known (read: clicked) term from hierarchy path
                setCurrentTerm(pathTerm);
                setCurrentHierarchyPath(tempHierarchyPath.current);
            } else {
                // create new term based on newUri
                setCurrentTerm(getCurrentTermFromUri(newUri));
                setCurrentHierarchyPath(undefined);
            }
        }
    }, [location, params, sparqlExecutor.versionUrl, selectedVersion]);

    // Load term data when currentTerm changes
    React.useEffect(() => {
        loadData(currentTerm);
    }, [currentTerm, loadData, sparqlExecutor.versionUrl, selectedVersion, params]);

    // Handlers
    const onTermSelected = (object: ITerm, parents: ITerm[]) => {
        tempHierarchyPath.current = [...parents, object];
        if (currentTerm && currentTerm.iri === object.iri) {
            setCurrentHierarchyPath(tempHierarchyPath.current);
        }
    };

    const onVersionSelect = (versionUrl: string) => {
        const version = versions.find((v: any) => v.url === versionUrl);

        sparqlExecutor.updateVersionUrl(version.url);
        setSelectedVersion(version.url);
        NavigateService.browse();
    };

    const getFormatedDate = (d: any) => {
        let date = new Date(d);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}/${month}/${day}`;
    };

    const versions = appConfig.versions;

    const renderVersionSelect = () => {
        if (!versions || !Array.isArray(versions)) {
            return null;
        }
        return (
            <FormControl
                variant="standard"
                sx={{ m: 1, minWidth: 200, maxWidth: 300 }}
                size="small"
            >
                <InputLabel id="version-select-label">Version</InputLabel>
                <Select
                    labelId="version-select-label"
                    id="version-select"
                    value={selectedVersion}
                    onChange={(e) => onVersionSelect(e.target.value)}
                    label="Version"
                    renderValue={(value) => {
                        const selectedVersion = versions.find((v) => v.url === value);
                        return selectedVersion ? (
                            <Tooltip
                                title={`Version: ${
                                    selectedVersion.version
                                }\nDate: ${getFormatedDate(selectedVersion.date)}`}
                            >
                                <span>{`Version: ${
                                    selectedVersion.version
                                } - Date: ${getFormatedDate(selectedVersion.date)}`}</span>
                            </Tooltip>
                        ) : (
                            "Unknown version"
                        );
                    }}
                >
                    {versions.map((version) => (
                        <MenuItem key={version.id} value={version.url}>
                            <Tooltip
                                key={version.id}
                                title={version.url.replace("/sparql", "")}
                                placement="right"
                                enterDelay={500}
                            >
                                <span style={{ width: "100%" }}>{`${
                                    version.version
                                } - ${getFormatedDate(version.date)}`}</span>
                            </Tooltip>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };
    const enableResize: Enable = {
        top: false,
        right: true, // Only enable the right handle for horizontal resizing
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false
    };

    const handleStyles = {
        right: {
            width: "6px",
            marginRight: "16px",
            borderRight: "1px solid rgb(204, 204, 204)",
            cursor: "col-resize" // Ensure the cursor changes to indicate resizability
        }
    };

    return (
        <div
            style={{
                height: "calc(100vh - 50px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
        >
            <TopMenu quicksearch />

            <Grid container style={{ overflow: "hidden", flexWrap: "nowrap", height: "100%" }}>
                <Resizable
                    size={{ width: hierarchyWidth, height: "100%" }}
                    minWidth={250}
                    maxWidth={800}
                    enable={enableResize}
                    onResizeStop={(e: any, direction: any, ref: any, d: { width: number }) => {
                        setHierarchyWidth(hierarchyWidth + d.width);
                    }}
                    style={{ overflow: "hidden" }}
                    handleStyles={handleStyles}
                    className="hierarchy-resizable"
                >
                    <Grid
                        className="hierarchy-column"
                        style={{ width: "95%", height: "calc(100% - 1em)" }}
                    >
                        {renderVersionSelect()}
                        <Tabs
                            value={hierarchies.findIndex((h) => h.name === activeHierarchy.name)}
                            onChange={(_, idx) => setActiveHierarchy(hierarchies[idx])}
                            // in case of tabs with scroll buttons
                            //      variant="scrollable"
                            //      scrollButtons={true}
                            //      allowScrollButtonsMobile={true}

                            // in case of all tabs always shown
                            slotProps={{ indicator: { sx: { display: "none" } } }}
                            sx={{
                                marginRight: "auto",
                                overflow: "visible",
                                minHeight: "auto",
                                "& .MuiTabs-flexContainer": {
                                    flexWrap: "wrap"
                                },
                                "& .MuiTabs-scroller": {
                                    overflow: "visible"
                                },
                                "& button": {
                                    padding: "4px 4px",
                                    margin: "4px 4px",
                                    minHeight: "auto",
                                    backgroundColor: "whitesmoke",
                                    "&:hover": {
                                        textDecoration: "underline"
                                    },
                                    "&.Mui-selected": {
                                        fontWeight: "bold",
                                        color: "rgba(0, 0, 0, 0.6)"
                                    }
                                }
                            }}
                        >
                            {hierarchies.map((hierarchy) => (
                                <Tab key={hierarchy.id} label={hierarchy.name} />
                            ))}
                        </Tabs>
                        {activeHierarchy.id && (
                            <Hierarchy
                                key={sparqlExecutor.versionUrl}
                                config={activeHierarchy}
                                currentTerm={currentTerm}
                                onSelect={onTermSelected}
                            />
                        )}
                    </Grid>
                </Resizable>
                <Grid
                    size={12}
                    className="content-column"
                    style={{
                        flexGrow: 1,
                        minWidth: 0,
                        height: "calc(100% - 1em)",
                        overflow: "auto"
                    }}
                >
                    <Breadcrumbs
                        config={activeHierarchy}
                        currentTerm={currentTerm}
                        path={currentHierarchyPath}
                        onSelect={onTermSelected}
                    />
                    <InformationPane
                        config={appConfig.config.informationPane}
                        currentTerm={currentTerm}
                    />
                </Grid>
            </Grid>
        </div>
    );
};
